<script setup lang="ts">
import { computed, useId } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    label: string
    /** Helper text shown under the control when there is no error. */
    hint?: string
    /** Tints the hint: `success` marks the outcome of an action, announced politely. */
    hintTone?: 'default' | 'success'
    /** Error message; replaces the hint, marks the control invalid and is announced politely. */
    error?: string
    /** Appends a "(Required)" marker to the label. */
    required?: boolean
    /** Appends an "(Optional)" marker to the label. */
    optional?: boolean
    id?: string
    /** Passed through to the control via the slot props for size symmetry. */
    size?: 'md' | 'lg'
    /** The label's element: `label` (default) names a labelable control through `for` · `caption`, a plain caption for a control that is not labelable, such as a radiogroup or a read-only value; the slot props then carry `aria-labelledby` pointing at it. */
    labelAs?: 'label' | 'caption'
  }>(),
  {
    hint: undefined,
    hintTone: 'default',
    error: undefined,
    required: false,
    optional: false,
    id: undefined,
    size: 'md',
    labelAs: 'label',
  },
)

const { t } = useI18n()

const generatedId = useId()
const fieldId = computed(() => props.id ?? generatedId)
const hintId = computed(() => `${fieldId.value}-hint`)
const errorId = computed(() => `${fieldId.value}-error`)
const labelId = computed(() => `${fieldId.value}-label`)
const isCaption = computed(() => props.labelAs === 'caption')

const describedBy = computed(() => {
  if (props.error) return errorId.value
  if (props.hint) return hintId.value
  return undefined
})

const controlProps = computed(() => ({
  id: fieldId.value,
  ariaInvalid: !!props.error,
  ariaDescribedby: describedBy.value,
  ...(isCaption.value ? { 'aria-labelledby': labelId.value } : {}),
}))
</script>

<template>
  <div class="group/field flex flex-col">
    <component
      :is="isCaption ? 'span' : 'label'"
      :id="isCaption ? labelId : undefined"
      :for="isCaption ? undefined : fieldId"
      :class="[
        'mb-4 text-body-sm-medium transition-colors duration-base ease-standard',
        error ? 'text-accent-red' : 'text-text-primary group-focus-within/field:text-accent-blue',
      ]"
    >
      {{ label }}
      <span v-if="required" class="text-caption font-normal text-text-tertiary">{{
        t('primitives.field.required')
      }}</span>
      <span v-else-if="optional" class="text-caption font-normal text-text-tertiary">{{
        t('primitives.field.optional')
      }}</span>
    </component>

    <slot v-bind="controlProps" />

    <div class="collapsible" :data-state="error ? 'open' : 'closed'">
      <div>
        <p
          v-if="error"
          :id="errorId"
          role="alert"
          aria-live="polite"
          class="pt-4 text-caption text-accent-red"
        >
          {{ error }}
        </p>
      </div>
    </div>
    <p
      v-if="hint && !error"
      :id="hintId"
      :aria-live="hintTone === 'success' ? 'polite' : undefined"
      :class="[
        'pt-4 text-caption',
        hintTone === 'success' ? 'text-accent-green' : 'text-text-tertiary',
      ]"
    >
      {{ hint }}
    </p>
  </div>
</template>
